import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserIcon,
  DocumentTextIcon,
  PrinterIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const SaleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    due_date: '',
    payment_method: 'cash',
    reference_number: '',
    notes: ''
  });

  useEffect(() => {
    fetchSale();
  }, [id]);

  const fetchSale = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/sales/transactions/${id}`);
      setSale(response.data);
    } catch (error) {
      toast.error('Failed to fetch sale details');
      navigate('/sales');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    if (paymentData.amount > sale.balance) {
      toast.error('Payment amount cannot exceed balance');
      return;
    }

    try {
      await api.post(`/sales/transactions/${id}/payments`, paymentData);
      toast.success('Payment added successfully');
      setShowPaymentModal(false);
      setPaymentData({
        amount: '',
        due_date: '',
        payment_method: 'cash',
        reference_number: '',
        notes: ''
      });
      fetchSale();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add payment');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmail = async () => {
    try {
      await api.post(`/sales/transactions/${id}/email`);
      toast.success('Invoice sent to customer email');
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!sale) return null;

  const canAddPayment = sale.status !== 'paid' && 
    (user?.user_type === 'sales_agent' || user?.user_type === 'shop_owner');

  return (
    <div>
      {/* Header */}
      <div className="mb-6 print:hidden">
        <button
          onClick={() => navigate('/sales')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Sales
        </button>
        
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-8 w-8 text-gray-400 mr-3" />
              <div>
                <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
                  {sale.transaction_number}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Created on {format(new Date(sale.created_at), 'dd MMM yyyy HH:mm')}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3 no-print">
            {canAddPayment && (
              <button
                onClick={() => setShowPaymentModal(true)}
                className="btn-primary"
              >
                <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                Add Payment
              </button>
            )}
            <button
              onClick={handlePrint}
              className="btn-secondary"
            >
              <PrinterIcon className="h-4 w-4 mr-2" />
              Print
            </button>
            <button
              onClick={handleEmail}
              className="btn-secondary"
            >
              <EnvelopeIcon className="h-4 w-4 mr-2" />
              Email
            </button>
          </div>
        </div>
      </div>

      {/* Invoice Content */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        {/* Header */}
        <div className="px-6 py-8 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">INVOICE</h1>
              <p className="mt-1 text-sm text-gray-500">{sale.transaction_number}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Date: {format(new Date(sale.transaction_date), 'dd MMM yyyy')}</p>
              {sale.due_date && (
                <p className="text-sm text-gray-500">Due Date: {format(new Date(sale.due_date), 'dd MMM yyyy')}</p>
              )}
              <div className="mt-2">
                <StatusBadge status={sale.status} />
              </div>
            </div>
          </div>
        </div>

        {/* Company & Customer Info */}
        <div className="px-6 py-6 border-b border-gray-200 grid grid-cols-2 gap-8">
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">From</h3>
            <p className="mt-2 text-lg font-medium text-gray-900">Beverage Distribution Ltd</p>
            <p className="text-sm text-gray-600">Main Depot, Chililabombwe</p>
            <p className="text-sm text-gray-600">Zambia</p>
            <p className="text-sm text-gray-600">Tel: +260 977 123 456</p>
            <p className="text-sm text-gray-600">Email: accounts@beveragedist.com</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">To</h3>
            <p className="mt-2 text-lg font-medium text-gray-900">{sale.shop_owner_name}</p>
            <p className="text-sm text-gray-600">{sale.shop_owner?.business_address}</p>
            <p className="text-sm text-gray-600">{sale.shop_owner?.town}</p>
            <p className="text-sm text-gray-600">Tel: {sale.shop_owner?.business_phone}</p>
          </div>
        </div>

        {/* Items Table */}
        <div className="px-6 py-6">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sale.items?.map((item) => (
                <tr key={item.id}>
                  <td className="py-4 text-sm text-gray-900">{item.product_name}</td>
                  <td className="py-4 text-sm text-gray-900 text-right">
                    {item.quantity_cases} cases
                    {item.quantity_bottles > 0 && ` + ${item.quantity_bottles} bottles`}
                  </td>
                  <td className="py-4 text-sm text-gray-900 text-right">ZMW {item.unit_price.toFixed(2)}</td>
                  <td className="py-4 text-sm text-gray-900 text-right">ZMW {item.total_price.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="px-6 py-6 border-t border-gray-200">
          <div className="flex justify-end">
            <div className="w-64 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">ZMW {sale.subtotal.toFixed(2)}</span>
              </div>
              {sale.discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-medium text-green-600">-ZMW {sale.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                <span>Total:</span>
                <span className="text-primary-600">ZMW {sale.total_amount.toFixed(2)}</span>
              </div>
              {sale.amount_paid > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Paid:</span>
                  <span>ZMW {sale.amount_paid.toFixed(2)}</span>
                </div>
              )}
              {sale.balance > 0 && (
                <div className="flex justify-between text-sm text-red-600 font-medium">
                  <span>Balance Due:</span>
                  <span>ZMW {sale.balance.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment History */}
        {sale.payments && sale.payments.length > 0 && (
          <div className="px-6 py-6 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Payment History</h3>
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sale.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="py-2 text-sm text-gray-900">
                      {format(new Date(payment.payment_date), 'dd MMM yyyy')}
                    </td>
                    <td className="py-2 text-sm text-gray-900 capitalize">
                      {payment.payment_method?.replace('_', ' ')}
                    </td>
                    <td className="py-2 text-sm text-gray-900">{payment.reference_number || '-'}</td>
                    <td className="py-2 text-sm text-gray-900 text-right">ZMW {payment.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Enhanced Payment History from JSON Notes */}
        {sale.notes && (() => {
          try {
            const notesData = typeof sale.notes === 'string' ? JSON.parse(sale.notes) : sale.notes;
            const paymentEntries = notesData.payment_entries || [];
            const summary = notesData.summary || {};
            
            if (paymentEntries.length === 0) {
              // If no payment entries, show notes as plain text
              return (
                <div className="px-6 py-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Notes</h3>
                  <p className="mt-2 text-sm text-gray-900">{sale.notes}</p>
                </div>
              );
            }
            
            return (
              <div className="px-6 py-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Payment History Details</h3>
                
                {/* Payment Summary Card */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Total Amount</p>
                      <p className="text-lg font-bold text-gray-900">ZMW {summary.total_amount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total Paid</p>
                      <p className="text-lg font-bold text-green-600">ZMW {summary.total_paid_to_date?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Remaining Balance</p>
                      <p className={`text-lg font-bold ${summary.remaining_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ZMW {summary.remaining_balance?.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Payment Count</p>
                      <p className="text-lg font-bold text-gray-900">{summary.payment_count}</p>
                    </div>
                  </div>
                </div>
                
                {/* Payment Entries Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date for Balance</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received By</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paymentEntries.map((entry, idx) => (
                        <tr key={idx} className={entry.is_final_payment ? 'bg-green-50' : ''}>
                          <td className="px-4 py-3 text-sm text-gray-900">{entry.payment_date}</td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600">
                            ZMW {entry.amount_paid?.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 capitalize">{entry.payment_method}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {entry.due_date_for_balance ? (
                              <span className="text-orange-600 font-medium">{entry.due_date_for_balance}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                            <p className="truncate" title={entry.notes}>
                              {entry.notes || '-'}
                            </p>
                            {entry.message && (
                              <p className="text-xs text-gray-400 mt-1">{entry.message}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{entry.received_by}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Payment Progress Bar */}
                {summary.total_amount > 0 && (
                  <div className="mt-6">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Payment Progress</span>
                      <span className="text-gray-600">
                        {((summary.total_paid_to_date / summary.total_amount) * 100).toFixed(1)}% Paid
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-green-600 h-2.5 rounded-full transition-all duration-500"
                        style={{ width: `${(summary.total_paid_to_date / summary.total_amount) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          } catch (e) {
            // If notes is not JSON, display as plain text
            return (
              <div className="px-6 py-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Notes</h3>
                <p className="mt-2 text-sm text-gray-900">{sale.notes}</p>
              </div>
            );
          }
        })()}

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 text-center text-sm text-gray-500">
          <p>Thank you for your business!</p>
          <p className="mt-1">This is a computer generated invoice. No signature required.</p>
        </div>
     
     
      </div>

      {/* Payment Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setPaymentData({
            amount: '',
            payment_method: 'cash',
            reference_number: '',
            notes: ''
          });
        }}
        title="Add Payment"
      >
        <form onSubmit={handleAddPayment}>
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Amount: ZMW {sale.total_amount.toFixed(2)}</p>
              <p className="text-sm text-gray-600">Amount Paid: ZMW {sale.amount_paid.toFixed(2)}</p>
              <p className="text-sm font-medium text-red-600">Balance: ZMW {sale.balance.toFixed(2)}</p>
            </div>

            <div>
              <label className="form-label">Payment Amount *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={sale.balance}
                required
                className="form-input"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value)})}
              />
            </div>

            {(paymentData.amount > 0 && sale.balance.toFixed(2) > paymentData.amount) && (
                              <div className="col-span-6 sm:col-span-3">
                                <label className="block text-sm font-medium text-gray-700">
                                  Due Date *
                                </label>
                                <input
                                  type="date"
                                  required
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                  value={paymentData.due_date}
                                  onChange={(e) => setPaymentData({...paymentData, due_date: e.target.value})}
                                  min={format(new Date(), 'yyyy-MM-dd')}
                                />
                              </div>
                            )}

            <div>
              <label className="form-label">Payment Method *</label>
              <select
                required
                className="form-input"
                value={paymentData.payment_method}
                onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
              >
                <option value="cash">Cash</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="check">Check</option>
              </select>
            </div>

            <div>
              <label className="form-label">Reference Number</label>
              <input
                type="text"
                className="form-input"
                value = {sale.transaction_number}
                readOnly
                //value={paymentData.reference_number}
                //onChange={(e) => setPaymentData({...paymentData, reference_number: e.target.value})}
                //placeholder="Transaction ID, Check Number, etc."
              />
            </div>

            <div>
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                rows="2"
                value={paymentData.notes}
                onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowPaymentModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Add Payment
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SaleDetails;